import { openInNewTab } from "@/utils/open-in-new-tab";
import { CellType } from "@/apis/ai/types";
import { patchUpdateCellInfo, resetTags } from "@/apis/table/route";
import {
  GetCellsListResponse,
  GetTagsFromCellResponse,
} from "@/apis/table/types";
import { isBetaFeatureEnabled } from "@/components/beta-feature";
import { useIsWorkbookAnalyzed } from "@/hooks/use-is-workbook-analyzed";
import { useCanRunWorkbookWideAction } from "@/hooks/use-can-run-workbook-wide-action";
import { useIsCurrentSheetAnalyzing } from "@/hooks/use-is-current-sheet-analyzing";
import { useIsCurrentSheetTaggingTarget } from "@/hooks/use-is-current-sheet-tagging-target";
import { useTaggingGuidance } from "@/hooks/use-tagging-guidance";
import { TaggingGuidanceHighlight } from "@/components/workspace/tagging-guidance-highlight";
import { IxdForwardingChip } from "@/components/workspace/ixd-forwarding-chip";
import useClearQueryCache from "@/hooks/use-clear-query-cache";
import { useCategoryId, useWorkbookId } from "@/hooks/use-workbook";
import { columnToNumber, parseCellAddress } from "@/jspreadsheet/utils";
import { cn } from "@/lib/utils";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { useSpreadsheetStore } from "@/stores/spreadsheet";
import "@/styles/vendors/jss.css";

import { AutoTagProgressModal } from "@/components/modals/auto-tag-progress-modal";
import { pushMisclickConfirmModal } from "@/components/modals/misclick-confirm-modal";
import useAutoTag from "@/hooks/mutations/use-auto-tag";
import {
  isAutoTagRunning,
  useAutoTagStatus,
} from "@/hooks/mutations/use-auto-tag-status";
import { useAutoTagWorkbook } from "@/hooks/mutations/use-auto-tag-workbook";
import { pushModal } from "@/stores/modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
} from "@/styles/ui/popover";
import "@/styles/vendors/jss.css";
import { Button } from "@/toggle-ui-kit/button";
import { Spreadsheet } from "@jspreadsheet-ce/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import jspreadsheet, {
  SpreadsheetInstance,
  WorksheetOptions,
} from "jspreadsheet-ce";
import {
  ChevronDown,
  Home,
  ListChecks,
  Loader2,
  Presentation,
  Sparkles,
  SquareDashedMousePointer,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { TabButton } from "../common/tab-select-button";
import { useTableAddress, useTableCells, useTableId } from "../mapping/hooks";
import { contextMenu } from "./context-menu";
import ForwardButton from "./forward";
import { useSpreadsheetEvent, useSpreadsheetHandler } from "./hooks";
import { useSpreadsheetData } from "./hooks/data";
import { useSelectedTab } from "./hooks/selected-tab";
import { useSheetEvents } from "./hooks/sheet-update";
import { usePreviewChangeTooltip } from "./hooks/tooltip";
import { toNoteLabel } from "./note-label";
import { TablePreviewTab } from "./preview-tab";
import { toast } from "sonner";
import { text } from "./text";

export const ViewSelectTab = ({ container }: { container: HTMLElement }) => {
  const { selectedTab, setSelectedTab } = useSelectedTab();
  const {
    data: { workbookId, tableId },
  } = useTableId();
  const queryClient = useQueryClient();
  const updateTab = (tab: "DSD" | "XBRL") => {
    setSelectedTab(tab);

    const dsdContainer = container.querySelector(
      ".jtabs-content",
    ) as HTMLElement;

    if (dsdContainer) {
      dsdContainer.style.display = tab === "DSD" ? "block" : "none";
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const [previewChangeTooltipShown, setPreviewChangeTooltipShown] = useState(
    sessionStorage.getItem("previewChangeTooltipShown") === "true"
      ? true
      : false,
  );

  const isMapping = useMappingStore(
    (state) => state.mappedInfos.mutation.mapping.isMapping,
  );
  const { isFullTagged } = usePreviewChangeTooltip();
  useEffect(() => {
    if (isMapping && isFullTagged && !previewChangeTooltipShown) {
      setIsOpen(true);
      setPreviewChangeTooltipShown(true);
      sessionStorage.setItem("previewChangeTooltipShown", "true");
    }
  }, [isMapping, isFullTagged, previewChangeTooltipShown, setIsOpen]);

  const fact = useMappingStore((state) => state.fact.selected);
  const clearSheet = useMappingStore((state) => state.mappedInfos.clearSheet);
  const { mutate: resetTagsMutate, isPending: isResettingTags } = useMutation({
    mutationFn: () => resetTags(workbookId!, tableId!),
    onSuccess: () => {
      if (fact?.sheet?.id) {
        clearSheet(fact.sheet.id);
      }

      if (!workbookId || !tableId) return;

      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId)._ctx.tables(tableId).queryKey,
      });
      // 표를 비우면 "빈 표"가 늘어 다음 액션 추천이 바뀔 수 있으니 함께 갱신한다.
      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId)._ctx.taggingGuidance().queryKey,
      });

      // cells 목록 캐시도 같은 자리에서 비운다. invalidate 의 refetch 는 비동기라, 응답이
      // 도착하기 전에 어떤 셀이 처음 선택되면 useSelectedCell 의 hydrate 가 아직 옛 태그를
      // 들고 있는 이 캐시를 정답으로 알고 store 에 심는다. 한번 심기면 hasMappedInfo 가
      // true 라 이후 hydrate 는 건너뛰고, mappedInfos.add 는 병합만 하므로 지워지지 않는다.
      queryClient.setQueryData<GetCellsListResponse>(
        queries.workbook(workbookId)._ctx.tables(tableId)._ctx.cells().queryKey,
        (oldData) =>
          oldData
            ? {
                ...oldData,
                cells: oldData.cells.map((cell) => ({
                  ...cell,
                  taggedInfos: { ...cell.taggedInfos, tags: [] },
                })),
              }
            : oldData,
      );

      // 모든 cells의 tags 쿼리 캐시 빈 배열로 설정해서 재설정 방지해줘야함.
      queryClient.setQueriesData<GetTagsFromCellResponse>(
        {
          predicate: (query) => {
            const key = query.queryKey as unknown[];
            return (
              key.includes(workbookId) &&
              key.includes(tableId) &&
              key.includes("tagged")
            );
          },
        },
        (oldData) =>
          oldData && Array.isArray(oldData.tags)
            ? { ...oldData, tags: [] }
            : oldData,
      );

      toast.success(`${sheetLabel}의 태그를 모두 해제했습니다.`);
    },
  });

  const handleResetTags = () => {
    if (!workbookId || !tableId) return;
    resetTagsMutate();
  };

  const { tableAddresses } = useTableAddress();
  const sheetLabel = toNoteLabel(
    tableAddresses?.tableAddresses.find((table) => table.tableId === tableId)
      ?.name,
  );

  // 해제할 태그가 없으면 되돌릴 것도 없으니 묻지 않는다. 셀 캐시가 정본 — 태깅
  // 뮤테이션이 무효화해 주므로 방금 한 태깅도 반영된다. 못 받았으면 묻는 쪽으로.
  const { cells } = useTableCells();
  // tags 만 본다 — reset-tags 는 tagged_infos.tags 만 비우고 roleKey·기간은 그대로다
  // (xbrl-back app/excel/domain/entity/table/cell.py Cell.reset_tags).
  const hasTagsToReset =
    !cells || cells.cells.some((cell) => cell.taggedInfos.tags.length > 0);

  const confirmResetTags = () => {
    if (!hasTagsToReset) {
      handleResetTags();
      return;
    }
    pushMisclickConfirmModal({
      title: `${sheetLabel}의 태그를 모두 해제할까요?`,
      description: (
        <>
          전체 선택을 해제하면 {sheetLabel}에 적용된 태그가 모두 해제됩니다.
          <br />
          해제한 태그는 되돌릴 수 없습니다.
        </>
      ),
      confirmLabel: "태그 모두 해제",
      onConfirm: handleResetTags,
    });
  };

  const { mutate: autoTag, isPending: isAutoTagging } = useAutoTag();
  const { mutate: autoTagWorkbook, isPending: isEnqueuingWorkbook } =
    useAutoTagWorkbook();
  // 읽기 전용 구독 — 폴링·완료 처리는 페이지 루트의 useAutoTagCompletionEffect 전담.
  const { data: autoTagStatus } = useAutoTagStatus(workbookId ?? undefined);
  const isWorkbookAnalyzed = useIsWorkbookAnalyzed(workbookId ?? undefined);
  // 전체 단위 게이트는 훅 하나가 소유한다 — 롤포워드 쪽과 같은 판정을 쓴다(WXBRL-2682).
  const canRunWorkbookWide = useCanRunWorkbookWideAction(
    workbookId ?? undefined,
  );
  const isCurrentSheetAnalyzing = useIsCurrentSheetAnalyzing();
  const isCurrentSheetTaggingTarget = useIsCurrentSheetTaggingTarget();
  const taggingRecommendation = useTaggingGuidance(
    workbookId ?? undefined,
    tableId,
  );
  const currentTableStatus = autoTagStatus?.tables.find(
    (table) => table.tableId === tableId,
  );
  // 버튼은 현재 표 기준 — 다른 표가 도는 중에도 이 표는 실행할 수 있다.
  const isCurrentTableAutoTagging =
    isAutoTagging ||
    (currentTableStatus ? isAutoTagRunning(currentTableStatus) : false);
  // "워크북 전체 실행 중"이 아니라 "표 하나라도 도는 중"이다. 표 단위 조건에
  // 섞으면 딴 표가 도는 동안 이 표가 잠긴다(WXBRL-2182).
  const isAnyTableAutoTagging =
    autoTagStatus?.tables.some(isAutoTagRunning) ?? false;

  // 오토태깅 실행 가능 조건. "진행 중" 판정도 분석 게이트도 대상 범위를 나눠서 본다.
  // 표 실행 조건에 워크북 단위 진행 여부를 섞으면 딴 표가 도는 동안 이 표가 잠기는
  // 회귀가 난다(WXBRL-2182).
  const isWorkbookReady = !!workbookId && isWorkbookAnalyzed;
  // 현재 시트 대상 액션만 그 시트의 분석 완료를 기다린다.
  const isCurrentSheetReady = isWorkbookReady && !isCurrentSheetAnalyzing;
  // 일반사항·회계정책·추정 등의 목차 시트에서는 "표 오토태깅"을 잠근다 — 서버도 거부하지만, 매핑 화면의
  // "블록태깅 대상 주석이에요" 안내와 버튼 상태가 같이 읽혀야 한다.
  const canAutoTagCurrentTable =
    isCurrentSheetReady &&
    isCurrentSheetTaggingTarget &&
    !!tableId &&
    !isCurrentTableAutoTagging &&
    !isEnqueuingWorkbook;
  // 전체 단위 게이트는 isWorkbookReady 에 섞지 않는다 — 딴 시트 분석 중에 이 표가 잠긴다(WXBRL-2182).
  const canAutoTagWorkbook =
    canRunWorkbookWide && !isAnyTableAutoTagging && !isEnqueuingWorkbook;

  return (
    <div className="custom-view-select-tab flex w-full px-[8px] h-[48px] items-center gap-[8px] justify-between min-w-[650px]">
      <div className="flex items-center gap-[8px]">
        <TabButton
          className="h-9 label-1-normal-medium text-[14px] py-2 px-4 rounded-[8px]"
          isActive={selectedTab === "DSD"}
          onClick={() => updateTab("DSD")}
        >
          태그 매핑
        </TabButton>
        <Popover open={isOpen}>
          <TabButton
            className={cn(
              "h-9 label-1-normal-medium text-[14px] py-2 px-4 rounded-[8px] relative",
              isOpen && "ring-[3px] ring-blue-500",
            )}
            isActive={selectedTab === "XBRL"}
            onClick={() => updateTab("XBRL")}
          >
            태그 빌딩
            <PopoverAnchor className="popover-anchor-preview-change-tooltip absolute top-10 -left-2" />
          </TabButton>
          <PopoverContent
            side="bottom"
            align="start"
            className="relative inline-flex py-[12px] px-[16px] justify-end gap-[12px] rounded-md bg-[#171717] shadow-lg w-fit border-none"
          >
            <div className="absolute -top-[4px] left-[12px] w-[12px] h-[12px] rotate-45 aspect-square bg-[#171717] rounded-sm" />
            <span className="text-white label-1-normal-medium">
              태깅 내용이 반영되었습니다.
              <br />
              미리보기에서 결과를 확인해 주세요.
            </span>
            <PopoverClose onClick={() => setIsOpen(false)}>
              <X className="w-[16px] h-[16px] flex-shrink-0 aspect-square stroke-white absolute right-[4px] top-[6px]" />
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex items-center gap-[8px]">
        <IxdForwardingChip workbookId={workbookId} />
        <Button
          variant="outlined"
          onClick={confirmResetTags}
          leftIcon={<SquareDashedMousePointer className="w-4 h-4" />}
          disabled={!workbookId || !tableId || isResettingTags}
        >
          전체 선택 해제
        </Button>
        <TaggingGuidanceHighlight
          key={`forward-${workbookId}-${taggingRecommendation}`}
          active={taggingRecommendation === "ROLL_FORWARD"}
          storageKey="ROLL_FORWARD"
          tooltip={
            "다시 입력할 필요 없이\n이전 태그 정보를 동일하게 불러올 수 있어요"
          }
        >
          <ForwardButton />
        </TaggingGuidanceHighlight>
        {/* 오토태깅: 단일 드롭다운 버튼 — 현재 표 / 전체 / 진행상황 */}
        <TaggingGuidanceHighlight
          key={`autotag-${workbookId}-${taggingRecommendation}`}
          active={taggingRecommendation === "AUTO_TAG"}
          storageKey="AUTO_TAG"
          tooltip={"AI가 남은 빈 표의\n태그를 자동으로 채워드려요"}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outlined"
                className="focus-visible:border-line-normal"
                leftIcon={
                  isCurrentTableAutoTagging || isEnqueuingWorkbook ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )
                }
                rightIcon={<ChevronDown className="w-4 h-4" />}
                disabled={!isCurrentSheetReady}
              >
                {isCurrentTableAutoTagging ? "오토태깅 중" : "오토태깅"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                disabled={!canAutoTagCurrentTable}
                onSelect={() => autoTag()}
              >
                <Sparkles className="w-4 h-4" />표 오토태깅
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                disabled={!canAutoTagWorkbook}
                onSelect={() => autoTagWorkbook()}
              >
                <Sparkles className="w-4 h-4" />
                전체 오토태깅
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                disabled={!workbookId}
                onSelect={() => pushModal(AutoTagProgressModal)}
              >
                <ListChecks className="w-4 h-4" />
                진행상황
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TaggingGuidanceHighlight>
        <Button
          variant="outlined"
          onClick={() =>
            workbookId &&
            openInNewTab(
              `/preview/${workbookId}` + (tableId ? `?tableId=${tableId}` : ""),
            )
          }
          leftIcon={<Presentation className="w-4 h-4" />}
        >
          미리보기
        </Button>
      </div>
    </div>
  );
};

export const useCustomViewSelectTab = (
  instance: SpreadsheetInstance | null,
  setHost: (host: HTMLDivElement | null) => void,
) => {
  useEffect(() => {
    if (!instance) return;

    const container = instance.el || instance.element;
    if (!container) return;

    const injectButton = () => {
      let tabsHeadersContainer = container.querySelector(
        ".jtabs-headers-container",
      );
      if (!tabsHeadersContainer) return;
      tabsHeadersContainer = tabsHeadersContainer.parentElement;
      if (!tabsHeadersContainer) return;

      let buttonContainer = tabsHeadersContainer.querySelector(
        ".custom-view-select-tab-container",
      ) as HTMLDivElement;

      if (!buttonContainer) {
        buttonContainer = document.createElement("div");
        buttonContainer.className = "custom-view-select-tab-container";
        tabsHeadersContainer.insertBefore(
          buttonContainer,
          tabsHeadersContainer.firstChild?.nextSibling as HTMLElement,
        );
      }
      setHost(buttonContainer);
    };

    const timer = setTimeout(injectButton, 100);

    const observer = new MutationObserver(() => {
      const existing = container.querySelector(
        ".custom-view-select-tab-container",
      );
      if (!existing) {
        injectButton();
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      const buttonContainer = container.querySelector(
        ".custom-view-select-tab-container",
      );
      if (buttonContainer) {
        buttonContainer.remove();
      }
      setHost(null);
    };
  }, [instance, setHost]);
};

export const useCustomTabButton = (
  instance: SpreadsheetInstance | null,
  setHost: (host: HTMLDivElement | null) => void,
) => {
  useEffect(() => {
    if (!instance) return;

    const container = instance.el || instance.element;
    if (!container) return;

    const injectButton = () => {
      const tabsHeadersContainer = container.querySelector(
        ".jtabs-headers-container",
      );
      if (!tabsHeadersContainer) return;

      let buttonContainer = tabsHeadersContainer.querySelector(
        ".custom-tab-button-container",
      ) as HTMLDivElement;

      if (!buttonContainer) {
        buttonContainer = document.createElement("div");
        buttonContainer.className = "custom-tab-button-container";
        tabsHeadersContainer.insertBefore(
          buttonContainer,
          tabsHeadersContainer.firstChild,
        );
      }
      setHost(buttonContainer);
    };

    const timer = setTimeout(injectButton, 100);

    const observer = new MutationObserver(() => {
      const existing = container.querySelector(".custom-tab-button-container");
      if (!existing) {
        injectButton();
      } else {
        setHost(existing as HTMLDivElement);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      const buttonContainer = container.querySelector(
        ".custom-tab-button-container",
      );
      if (buttonContainer) {
        buttonContainer.remove();
      }
      setHost(null);
    };
  }, [instance, setHost]);
};

const CustomTabButton = () => {
  const navigate = useNavigate();
  const clearQueryCache = useClearQueryCache();
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    clearQueryCache();
    navigate("/workbooks");
  };
  return (
    <button
      type="button"
      className="custom-tab-button"
      onClick={handleClick}
      aria-label="Add new tab"
    >
      <Home className="w-4 h-4" />
    </button>
  );
};

/**
 * useSelectedSheetElement
 *
 * jspreadsheet의 시트 전환은 비동기적으로 이루어지므로,
 * DOM의 .jtabs-selected 요소가 실제로 렌더링될 때까지 기다려야 함.
 *
 * 이 훅은 MutationObserver를 사용하여:
 * 1. 초기 로드 시 시트가 렌더링될 때까지 대기
 * 2. 탭 전환 시 새 시트가 렌더링될 때까지 대기
 *
 * 반환되는 sheetElement는 실제 렌더링된 시트 HTML Element임
 * 해당 tableId와 일치하는 시트임이 보장됨.
 */
const useSelectedSheetElement = (tableId: string | null) => {
  const [sheetElement, setSheetElement] = useState<Element | null>(null);
  const activeSheet = useSpreadsheetStore((state) => state.activeSheet);

  useEffect(() => {
    if (!tableId) {
      setSheetElement(null);
      return;
    }

    // activeSheet의 tableId가 현재 tableId와 일치하는지 확인
    const activeSheetTableId = activeSheet?.options?.meta?.info?.tableId;

    const checkAndSetSheet = () => {
      const selectedSheet = document.querySelector(
        ".jtabs-content > .jtabs-selected",
      );

      if (selectedSheet && activeSheetTableId === tableId) {
        setSheetElement(selectedSheet);
        return true;
      }
      return false;
    };

    if (checkAndSetSheet()) {
      return;
    }

    const tabsContent = document.querySelector(".jtabs-content");
    if (!tabsContent) {
      const bodyObserver = new MutationObserver(() => {
        const content = document.querySelector(".jtabs-content");
        if (content) {
          bodyObserver.disconnect();
          checkAndSetSheet();
        }
      });

      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => bodyObserver.disconnect();
    }

    const observer = new MutationObserver(() => {
      checkAndSetSheet();
    });

    observer.observe(tabsContent, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, [tableId, activeSheet]);

  return sheetElement;
};

const useCellStyles = () => {
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useTableId();

  const { data: cells } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: !!workbookId && !!tableId,
    select: (data) => {
      return {
        tableId: tableId,
        cells: data.cells,
      };
    },
  });

  const queryClient = useQueryClient();
  const selectedSheet = useSelectedSheetElement(tableId);

  const { mutate: updateCellInfo } = useMutation({
    mutationFn: ({
      cellAddress,
      type,
    }: {
      cellAddress: string;
      type: CellType;
    }) => {
      return patchUpdateCellInfo(workbookId!, tableId!, cellAddress, {
        type,
        definitionRoleKey: null,
      });
    },
    onSuccess: ({ cellAddress, type }) => {
      queryClient.setQueryData(
        queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells()
          .queryKey,
        (old: GetCellsListResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            cells: old.cells.map((cell) => {
              if (cell.address === cellAddress) {
                return { ...cell, type };
              }
              return cell;
            }),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId!).queryKey,
      });
    },
  });

  const getCellElement = useCallback(
    (sheetElement: Element, address: string) => {
      if (address.includes(":")) {
        address = address.split(":")[0];
      }
      const { column, row } = parseCellAddress(address);
      const colIndex = columnToNumber(column) - 1;
      const rowIndex = row - 1;
      return sheetElement.querySelector(
        `td[data-x="${colIndex}"][data-y="${rowIndex}"]`,
      ) as HTMLElement | null;
    },
    [],
  );

  const removeRoleKeyBackgroundColor = useCallback(
    (cellElement: HTMLElement) => {
      cellElement.classList.remove("definition-cell-0");
      cellElement.classList.remove("definition-cell-1");
      cellElement.classList.remove("definition-cell-2");
      cellElement.classList.remove("definition-cell-3");
    },
    [],
  );

  const applyRoleKeyBackgroundColor = useCallback(
    (cellElement: HTMLElement, classSuffixIndex: number) => {
      removeRoleKeyBackgroundColor(cellElement);
      cellElement.classList.add(`definition-cell-${classSuffixIndex}`);
    },
    [removeRoleKeyBackgroundColor],
  );

  useLayoutEffect(() => {
    if (!cells || !selectedSheet) return;

    // cells.tableId와 tableId가 일치하는지 확인 (데이터와 DOM 동기화)
    if (cells.tableId !== tableId) return;

    // Store handler references for proper cleanup
    const handlers = new Map<string, (e: Event) => void>();

    cells.cells.forEach((cell) => {
      const cellElement = getCellElement(selectedSheet, cell.address);
      if (!cellElement) return;

      // Alt(Option)+우클릭으로만 cell type 전환. 일반 우클릭은 기본 동작.
      const handler = (e: Event) => {
        if (!(e instanceof MouseEvent) || !e.altKey) return;
        e.preventDefault();
        updateCellInfo({
          cellAddress: cell.address,
          type: CellType.HEADER,
        });
        removeRoleKeyBackgroundColor(cellElement);
      };

      handlers.set(cell.address, handler);
      cellElement.addEventListener("contextmenu", handler);

      if (!cell.taggedInfos.roleKey) return;

      const isTaggableCell =
        cell.type === CellType.DATA ||
        cell.type === CellType.SUM ||
        cell.type === CellType.HEADER;

      // 태깅 여부에 따라 스타일 변경
      const isDataCellTagged =
        cell.taggedInfos.tags.some((tag) => tag.type === "MEMBER") &&
        cell.taggedInfos.tags.some((tag) => tag.type === "LINE_ITEM");

      if (isDataCellTagged) {
        cellElement.classList.add("tagged-cell");
      } else {
        cellElement.classList.remove("tagged-cell");
      }

      if (!isTaggableCell) {
        removeRoleKeyBackgroundColor(cellElement);
        return;
      }

      if (cell.type === CellType.SUM) {
        cellElement.style.position = "relative";
        const overlay = document.createElement("div");

        overlay.className = "cell-overlay-sum";
        cellElement.appendChild(overlay);
      }

      let periodOverlay: HTMLDivElement | null = null;
      if (
        (cell.type === CellType.DATA || cell.type === CellType.SUM) &&
        (cell.taggedInfos.period === "BEGIN" ||
          cell.taggedInfos.period === "END")
      ) {
        cellElement.style.position = "relative";
        periodOverlay = document.createElement("div");
        const isBegin = cell.taggedInfos.period === "BEGIN";

        periodOverlay.className = `cell-overlay-period ${
          isBegin ? "cell-overlay-period-begin" : "cell-overlay-period-end"
        }${cell.type === CellType.SUM ? " has-sum" : ""}`;
        periodOverlay.textContent = isBegin ? "기초" : "기말";
        cellElement.appendChild(periodOverlay);
      }

      // roll-forward 의 'review' confidence 배지 — LLM 이 검토 권장한 cell.
      // 'exact' 는 noise 라 표시 안 함. period overlay 가 있으면 그 안에 inline
      // child 로 박아 "기초"/"기말" 칩 바로 옆에 자연스럽게 붙고, period 가 없는
      // cell 은 좌측 가운데 standalone 으로 fallback.
      if (isBetaFeatureEnabled() && cell.rollForwardConfidence === "review") {
        const chip = document.createElement("span");
        chip.className =
          "beta-feature-target cell-overlay-roll-forward cell-overlay-roll-forward-review";
        chip.textContent = "?";
        chip.title = "Roll-forward · 검토 필요";

        if (periodOverlay) {
          periodOverlay.appendChild(chip);
        } else {
          cellElement.style.position = "relative";
          chip.classList.add("standalone");
          if (cell.type === CellType.SUM) {
            chip.classList.add("has-sum");
          }
          cellElement.appendChild(chip);
        }
      }
    });

    // 당기/전기 영역 보더
    const applyYearBorder = (
      targetYearValue: "CURRENT" | "PRIOR",
      prefix: string,
    ) => {
      // 그룹: 진짜 매핑된 데이터/합계 셀만 (DATA/SUM + roleKey + targetYear 일치)
      const group = cells.cells.filter(
        (c) =>
          c.taggedInfos.targetYear === targetYearValue &&
          (c.type === CellType.DATA || c.type === CellType.SUM) &&
          c.taggedInfos.roleKey !== null,
      );
      if (group.length === 0) return;

      const normalizeAddress = (address: string) =>
        address.includes(":") ? address.split(":")[0] : address;

      const getSpan = (address: string) => {
        const el = getCellElement(selectedSheet, address);
        return {
          rowspan: Math.max(
            1,
            parseInt(el?.getAttribute("rowspan") ?? "1") || 1,
          ),
          colspan: Math.max(
            1,
            parseInt(el?.getAttribute("colspan") ?? "1") || 1,
          ),
        };
      };

      // 그룹 셀들이 점유하는 모든 위치를 set으로 색인
      const groupSet = new Set<string>();
      const groupCells = group.map((c) => {
        const startAddr = normalizeAddress(c.address);
        const { column, row } = parseCellAddress(startAddr);
        const col = columnToNumber(column);
        const { rowspan, colspan } = getSpan(startAddr);
        for (let r = row; r < row + rowspan; r++) {
          for (let cc = col; cc < col + colspan; cc++) {
            groupSet.add(`${r},${cc}`);
          }
        }
        return { address: startAddr, row, col, rowspan, colspan };
      });

      // 각 그룹 셀에서 인접 위치가 같은 그룹에 속하지 않으면 그쪽 변에 보더
      // → 인접 그룹 셀끼리는 보더 suppress, 외곽만 그려짐
      // → 떨어진 그룹들은 자연스럽게 별개의 외곽선으로 분리됨
      groupCells.forEach((p) => {
        const el = getCellElement(selectedSheet, p.address);
        if (!el) return;
        let topEnclosed = true;
        let bottomEnclosed = true;
        for (let c = p.col; c < p.col + p.colspan; c++) {
          if (!groupSet.has(`${p.row - 1},${c}`)) topEnclosed = false;
          if (!groupSet.has(`${p.row + p.rowspan},${c}`))
            bottomEnclosed = false;
        }
        let leftEnclosed = true;
        let rightEnclosed = true;
        for (let r = p.row; r < p.row + p.rowspan; r++) {
          if (!groupSet.has(`${r},${p.col - 1}`)) leftEnclosed = false;
          if (!groupSet.has(`${r},${p.col + p.colspan}`)) rightEnclosed = false;
        }
        if (!topEnclosed) el.classList.add(`${prefix}-top`);
        if (!bottomEnclosed) el.classList.add(`${prefix}-bottom`);
        if (!leftEnclosed) el.classList.add(`${prefix}-left`);
        if (!rightEnclosed) el.classList.add(`${prefix}-right`);
      });
    };

    applyYearBorder("CURRENT", "year-border-current");
    applyYearBorder("PRIOR", "year-border-prior");

    return () => {
      cells.cells.forEach((cell) => {
        const cellElement = getCellElement(selectedSheet, cell.address);
        if (!cellElement) return;

        cellElement.classList.remove("tagged-cell");

        // Remove event listener with correct reference
        const handler = handlers.get(cell.address);
        if (handler) {
          cellElement.removeEventListener("contextmenu", handler);
        }

        if (cell.type === CellType.SUM || cell.type === CellType.DATA) {
          cellElement.style.position = "relative";
          const overlay = cellElement.querySelector(".cell-overlay-sum");
          if (overlay) {
            overlay.remove();
          }
        }

        const periodOverlay = cellElement.querySelector(".cell-overlay-period");
        if (periodOverlay) {
          periodOverlay.remove();
        }

        const rollForwardOverlay = cellElement.querySelector(
          ".cell-overlay-roll-forward",
        );
        if (rollForwardOverlay) {
          rollForwardOverlay.remove();
        }

        cellElement.classList.remove(
          "year-border-current-top",
          "year-border-current-right",
          "year-border-current-bottom",
          "year-border-current-left",
          "year-border-prior-top",
          "year-border-prior-right",
          "year-border-prior-bottom",
          "year-border-prior-left",
        );
      });
      handlers.clear();
    };
  }, [
    cells,
    selectedSheet,
    tableId,
    updateCellInfo,
    getCellElement,
    removeRoleKeyBackgroundColor,
  ]);

  // role 변경 후 cell 배경색 in-place 갱신.
  // useSpreadsheetData 가 worksheet 옵션의 classes 맵으로 색을 입히지만 jss 는
  // init 시점 옵션만 받으므로 cells refetch 만으로는 색이 안 바뀐다. 같은 알고리즘
  // (suffix → 등장 순서 index)으로 definition-cell-X 클래스를 직접 토글.
  useLayoutEffect(() => {
    if (!cells || !selectedSheet) return;
    if (cells.tableId !== tableId) return;

    const defRoleSuffixes = new Set<string>();
    cells.cells.forEach((cell) => {
      if (cell.taggedInfos.roleKey) {
        const suffix =
          cell.taggedInfos.roleKey.split("_").pop() ?? cell.taggedInfos.roleKey;
        defRoleSuffixes.add(suffix);
      }
    });
    const suffixList = Array.from(defRoleSuffixes);

    cells.cells.forEach((cell) => {
      const cellElement = getCellElement(selectedSheet, cell.address);
      if (!cellElement) return;

      // SECTION/TEXTBLOCK 은 role 배경색을 칠하지 않는다 (DATA/SUM 만).
      const isColorableType =
        cell.type === CellType.DATA || cell.type === CellType.SUM;

      if (!isColorableType || !cell.taggedInfos.roleKey) {
        removeRoleKeyBackgroundColor(cellElement);
        return;
      }

      const suffix =
        cell.taggedInfos.roleKey.split("_").pop() ?? cell.taggedInfos.roleKey;
      const idx = suffixList.indexOf(suffix);
      applyRoleKeyBackgroundColor(cellElement, idx);
    });

    // jss 가 시트 전환 시 init 옵션 기반으로만 className 을 sync 하므로, React 가
    // 직접 박은 definition-cell-X 는 jss 의 관리 밖. cleanup 없으면 이전 시트
    // selectedSheet 의 td 에 잔존해 다음 시트 화면에 색이 새어 나옴.
    return () => {
      cells.cells.forEach((cell) => {
        const cellElement = getCellElement(selectedSheet, cell.address);
        if (cellElement) removeRoleKeyBackgroundColor(cellElement);
      });
    };
  }, [
    cells,
    selectedSheet,
    tableId,
    getCellElement,
    removeRoleKeyBackgroundColor,
    applyRoleKeyBackgroundColor,
  ]);
};

export const Workspace = ({ categoryId }: { categoryId: string | null }) => {
  const spreadsheetRefs = useRef<any[]>([]);
  useCellStyles();
  useSpreadsheetEvent();

  const [spreadsheetInstance, setSpreadsheetInstance] = useState<any>(null);
  const [viewSelectHost, setViewSelectHost] = useState<HTMLDivElement | null>(
    null,
  );
  const [tabButtonHost, setTabButtonHost] = useState<HTMLDivElement | null>(
    null,
  );
  const { data, isLoading, isError } = useSpreadsheetData(categoryId);
  const { selectedTab, setSelectedTab } = useSelectedTab();

  useCustomTabButton(spreadsheetInstance, setTabButtonHost);
  useCustomViewSelectTab(spreadsheetInstance, setViewSelectHost);
  const { handleSelectionChange, handleLoad: hookHandleLoad } =
    useSpreadsheetHandler();
  const { onafterchanges, onchange, oncreateeditor } = useSheetEvents();

  const handleLoad = useCallback(
    (instance: SpreadsheetInstance) => {
      if (jspreadsheet.spreadsheet) {
        if (!jspreadsheet.spreadsheet.includes(instance)) {
          jspreadsheet.spreadsheet.push(instance);
        }
      }

      setSpreadsheetInstance(instance);
      hookHandleLoad(instance);
    },
    [hookHandleLoad],
  );

  // Cleanup spreadsheet instance on unmount
  useEffect(() => {
    return () => {
      if (!spreadsheetInstance || !jspreadsheet.spreadsheet) {
        return;
      }
      const element =
        spreadsheetInstance.element ?? spreadsheetInstance.el ?? null;
      if (!element || !element.parentNode) {
        return;
      }
      const index = jspreadsheet.spreadsheet.indexOf(spreadsheetInstance);
      if (index > -1) {
        jspreadsheet.spreadsheet.splice(index, 1);
      }
      jspreadsheet.destroy(element, true);
    };
  }, [spreadsheetInstance]);

  const {
    data: { workbookId, tableId },
  } = useTableId();

  const { data: activeCategoryId } = useCategoryId();

  useEffect(() => {
    setSelectedTab("DSD");
  }, [activeCategoryId, setSelectedTab]);

  if (isLoading || !data || data.length === 0 || isError) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!categoryId) {
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const dataByCategory = data.reduce(
    (acc, item) => {
      const key = item.meta?.info?.categoryId as string;
      if (key) {
        acc[key] = [...(acc[key] || []), item];
      }
      return acc;
    },
    {} as Record<string, WorksheetOptions[]>,
  );

  jspreadsheet.setDictionary(text);
  return (
    <div className="flex-1 h-full flex flex-col">
      {viewSelectHost &&
        (spreadsheetInstance?.el || spreadsheetInstance?.element) &&
        createPortal(
          <ViewSelectTab
            container={spreadsheetInstance?.el || spreadsheetInstance?.element}
          />,
          viewSelectHost,
        )}
      {tabButtonHost &&
        createPortal(
          <div>
            <div className="flex h-full w-[41px] items-center justify-center">
              <CustomTabButton />
            </div>
          </div>,
          tabButtonHost,
        )}
      {Object.entries(dataByCategory).map(
        ([currentCategoryId, worksheets], index) => {
          if (currentCategoryId !== categoryId) return null;
          return (
            <Spreadsheet
              tabs
              contextMenu={contextMenu}
              parseHTML={true}
              key={currentCategoryId}
              ref={(el: any) => {
                spreadsheetRefs.current[index] = el;
              }}
              onafterchanges={onafterchanges}
              onchange={onchange}
              oncreateeditor={oncreateeditor}
              onselection={handleSelectionChange}
              onload={handleLoad}
              worksheets={worksheets}
            />
          );
        },
      )}
      <div className="flex-1 h-full flex items-center justify-center text-gray-500 label-1-normal-medium relative">
        {!!workbookId && !!tableId && selectedTab === "XBRL" && (
          <TablePreviewTab workbookId={workbookId} tableId={tableId} />
        )}
      </div>
    </div>
  );
};
